import { useEffect, useRef, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { FilterControllerButton } from '@insights_module/components/episode_details_screen/FilterControllerButton';
import { CaretIconUp } from '@icons/CaretIconUp';
import { CaretIcon } from '@icons/CaretIcon';
import { CloseIcon } from '@icons/CloseIcon';
import {
  getElementsByContainingText,
  getSpanElementBySpanIndex,
  highlightMatchingSubstrings,
  removeMatchingSubstringHighlighting,
} from '@insights_module/utils/episodesUtils';
import { useDebouncedState } from '@app/hooks/useDebounce';
import { SearchIcon } from '@icons/SearchIcon';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { SPAN_DATA_NAME } from '@insights_module/constants/filterControllers';
import { Spacer } from '@components/Spacer';

const Container = tw.div`w-[250px] flex flex-row justify-between items-center bg-neutral-background rounded border border-gray-1  cursor-pointer transition-all duration-300 ease-in-out overflow-hidden`;
const IconWrapper = tw.div`flex justify-center items-center min-w-[32px] min-h-[32px] ml-[-1px]`;
const Separator = tw.div`w-[0px] h-[19px] bg-gray-1 border-l border-gray-1 rounded-full`;
const ActionsWrapper = tw.div`flex flex-row items-center gap-1 pr-2`;
const SearchMentionsCount = tw.p`text-[11px] text-text-and-icon-muted`;
const CloseButton = tw.div`flex justify-center items-center w-[12px] h-[12px] cursor-pointer`;

const highlightedElementClassSet = ['selected-highlighted-text'];

export const SearchFilterController = () => {
  const { t } = useTranslation();
  const {
    setSelectedEntity,
    setSelectedGarmCategory,
    searchTerm,
    setSearchTerm,
    setSelectedSegment,
    setIsAudioPlaying,
  } = useEpisodeDetailsContext();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [selectedSpanIndex, setSelectedSpanIndex] = useState<number>(0);
  const [elements, setElements] = useState<HTMLElement[]>([]);
  const debouncedSearch: string = useDebouncedState(searchTerm, 500);

  const inputRef = useRef<HTMLInputElement>(null);

  const hasFullWidth = isFocused || searchTerm.length > 0;

  const handleIconClick = () => {
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    setSelectedEntity(null);
    setSelectedGarmCategory(null);
    setSelectedSegment(null);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAudioPlaying(false);
    setSearchTerm(e.target.value);
  };

  const handlePrev = () => {
    setIsAudioPlaying(false);
    if (!searchTerm) return;
    removeHighlightedElementClassSet();

    if (selectedSpanIndex > 0) {
      setSelectedSpanIndex(selectedSpanIndex - 1);
      return;
    }

    setSelectedSpanIndex(elements.length - 1);
  };

  const handleNext = () => {
    setIsAudioPlaying(false);
    if (!searchTerm) return;
    removeHighlightedElementClassSet();

    if (selectedSpanIndex < elements.length - 1) {
      setSelectedSpanIndex(selectedSpanIndex + 1);
      return;
    }

    setSelectedSpanIndex(0);
  };

  const handleClose = () => {
    removeHighlightedElementClassSet();
    setSelectedSpanIndex(0);
    setSearchTerm('');
  };

  useEffect(() => {
    const transcript = document.getElementById('transcript');
    if (!transcript) return;
    removeMatchingSubstringHighlighting(transcript, SPAN_DATA_NAME.SELECTED_SEARCH_TERM);

    if (!debouncedSearch) return;

    const elements = getElementsByContainingText(transcript, debouncedSearch, false);
    highlightMatchingSubstrings(
      elements,
      debouncedSearch,
      ['highlighted-text'],
      SPAN_DATA_NAME.SELECTED_SEARCH_TERM,
      false
    );

    setSelectedSpanIndex(0);
    setElements(elements);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!elements.length) return;

    const span = getSpanElementBySpanIndex(elements, selectedSpanIndex);
    span?.classList.add(...highlightedElementClassSet);

    // need to wait for the DOM to update before scrolling
    const timeoutId = setTimeout(() => {
      span?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedSpanIndex, elements]);

  const removeHighlightedElementClassSet = () => {
    if (!elements.length) return;

    const span = getSpanElementBySpanIndex(elements, selectedSpanIndex);
    span?.classList.remove(...highlightedElementClassSet);
  };

  return (
    <Container css={[!hasFullWidth && tw`w-[32px] h-[32px]`]}>
      <IconWrapper onClick={handleIconClick}>
        <SearchIcon tw='stroke-text-and-icon-muted' />
      </IconWrapper>
      <input
        tw='w-full focus:outline-none bg-transparent text-xs text-text-and-icon'
        placeholder={t('insights.episode_details_drawer.search_filter_placeholder')}
        ref={inputRef}
        value={searchTerm}
        onChange={handleSearch}
        onFocus={() => handleFocus()}
        onBlur={() => setIsFocused(false)}
      />
      <Spacer direction='horizontal' size='xxs' />
      <ActionsWrapper>
        {!!searchTerm.length && (
          <SearchMentionsCount>
            {`${elements.length ? selectedSpanIndex + 1 : 0}/${elements.length}`}
          </SearchMentionsCount>
        )}

        <Separator />

        <FilterControllerButton tw='min-h-[18px] min-w-[18px]' onClick={handlePrev}>
          <CaretIconUp tw='stroke-text-and-icon w-2' />
        </FilterControllerButton>
        <FilterControllerButton tw='min-h-[18px] min-w-[18px]' onClick={handleNext}>
          <CaretIcon tw='fill-text-and-icon w-2' />
        </FilterControllerButton>

        <CloseButton onClick={handleClose}>
          <CloseIcon tw='fill-text-and-icon w-[8px] h-[8px]' />
        </CloseButton>
      </ActionsWrapper>
    </Container>
  );
};
